import { useEffect, useState } from "react";
import {
    Box, Button, Typography, Paper, Stack, Chip, IconButton,
    Tooltip, Divider, Collapse
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { KanbanModulo } from "./TabKanbanModulos";
import ModalKanbanTarefa, { KanbanGrupo } from "./ModalKanbanTarefa";

export default function TabKanbanTarefas() {
    const [openLoad, setOpenLoad] = useState(false);
    const [modulos, setModulos] = useState<KanbanModulo[]>([]);
    const [grupos, setGrupos] = useState<KanbanGrupo[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [editandoGrupo, setEditandoGrupo] = useState<KanbanGrupo | null>(null);
    const [expandedGrupos, setExpandedGrupos] = useState<Record<number, boolean>>({});

    async function loadModulos() {
        try {
            const res = await api.get('/kanbanmodulos');
            if (res.data && !res.data.error) setModulos(res.data);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar módulos' });
        }
    }

    async function loadGrupos() {
        try {
            const res = await api.get('/kanbangrupos');
            if (res.data && !res.data.error) setGrupos(res.data);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar grupos' });
        }
    }

    async function loadAll() {
        setOpenLoad(true);
        await Promise.all([loadModulos(), loadGrupos()]);
        setOpenLoad(false);
    }

    useEffect(() => { loadAll(); }, []);

    function handleNovoGrupo() {
        if (modulos.length === 0) {
            return AlertInfo({
                icon: 'info',
                title: 'Cadastre pelo menos um módulo na aba "Módulos" antes de criar tarefas.',
            });
        }
        setEditandoGrupo(null);
        setOpenModal(true);
    }

    function handleEditarGrupo(grupo: KanbanGrupo) {
        setEditandoGrupo(grupo);
        setOpenModal(true);
    }

    async function handleDeletarGrupo(grupo: KanbanGrupo) {
        AlertConfirm({ title: `Deseja deletar o grupo "${grupo.nome_grupo}" e todas as suas tarefas?` }).then(async res => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const r = await api.delete(`/deletekanbangrupo/${grupo.id_kanban_grupo}`);
                    if (r.data.success) {
                        AlertInfo({ icon: 'success', title: 'Grupo deletado!' });
                        loadGrupos();
                    } else {
                        AlertInfo({ icon: 'error', title: r.data.message });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexão' });
                }
                setOpenLoad(false);
            }
        });
    }

    async function handleDuplicarGrupo(grupo: KanbanGrupo) {
        AlertConfirm({ title: `Deseja duplicar o grupo "${grupo.nome_grupo}"?` }).then(async res => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const r = await api.post('/createkanbangrupo', {
                        nome_grupo: `${grupo.nome_grupo} (cópia)`,
                        tarefas: grupo.tarefas.map(t => ({
                            id_kanban_modulo: t.id_kanban_modulo,
                            nome_tarefa: t.nome_tarefa,
                            tempo_execucao_horas: t.tempo_execucao_horas,
                            tempo_definido_usuario: t.tempo_definido_usuario,
                        })),
                    });
                    if (r.data.success) {
                        AlertInfo({ icon: 'success', title: 'Grupo duplicado!' });
                        loadGrupos();
                    } else {
                        AlertInfo({ icon: 'error', title: r.data.message });
                    }
                } catch {
                    AlertInfo({ icon: 'error', title: 'Falha na conexão' });
                }
                setOpenLoad(false);
            }
        });
    }

    function getModuloNome(id_kanban_modulo: number): string {
        return modulos.find(m => m.id_kanban_modulo === id_kanban_modulo)?.nome_modulo || 'Módulo removido';
    }

    function tarefasPorModulo(grupo: KanbanGrupo) {
        const map = new Map<number, typeof grupo.tarefas>();
        grupo.tarefas.forEach(t => {
            if (!map.has(t.id_kanban_modulo)) map.set(t.id_kanban_modulo, []);
            map.get(t.id_kanban_modulo)!.push(t);
        });
        const moduloIds = modulos.map(m => m.id_kanban_modulo);
        return Array.from(map.entries()).sort(([a], [b]) => moduloIds.indexOf(a) - moduloIds.indexOf(b));
    }

    return (
        <>
            <SimpleBackdrop open={openLoad} />

            <ModalKanbanTarefa
                open={openModal}
                onClose={() => { setOpenModal(false); setEditandoGrupo(null); }}
                onSaved={loadGrupos}
                modulos={modulos}
                grupo={editandoGrupo}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Grupos de Tarefas</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gerencie os grupos de tarefas dos módulos kanban
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon />}
                    onClick={handleNovoGrupo}
                    sx={{ textTransform: 'none' }}
                >
                    Novo Grupo
                </Button>
            </Stack>

            {/* Estado vazio */}
            {grupos.length === 0 && !openLoad && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                        Nenhum grupo de tarefas cadastrado
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                        {modulos.length === 0
                            ? 'Cadastre módulos primeiro na aba "Módulos"'
                            : 'Clique em "Novo Grupo" para começar'
                        }
                    </Typography>
                </Box>
            )}

            {/* Lista de grupos */}
            <Stack spacing={2}>
                {grupos.map(grupo => {
                    const modulosTarefas = tarefasPorModulo(grupo);
                    const totalTarefas = grupo.tarefas.length;

                    return (
                        <Paper key={grupo.id_kanban_grupo} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            {/* Header do grupo */}
                            <Box
                                sx={{ px: 2, py: 1.5, bgcolor: '#f5f5f5', borderBottom: '1px solid', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: '#eeeeee' } }}
                                onClick={() => setExpandedGrupos(prev => ({ ...prev, [grupo.id_kanban_grupo]: !prev[grupo.id_kanban_grupo] }))}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    {expandedGrupos[grupo.id_kanban_grupo]
                                        ? <ExpandLessIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                        : <ExpandMoreIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                    }
                                    <FolderIcon color="primary" sx={{ fontSize: 22 }} />
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
                                        {grupo.nome_grupo}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`${totalTarefas} tarefa${totalTarefas !== 1 ? 's' : ''}`}
                                        color="primary"
                                        sx={{ fontSize: 11, height: 22 }}
                                    />
                                    <Tooltip title="Editar grupo">
                                        <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleEditarGrupo(grupo); }}>
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Duplicar grupo">
                                        <IconButton size="small" sx={{ color: '#757575' }} onClick={(e) => { e.stopPropagation(); handleDuplicarGrupo(grupo); }}>
                                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Deletar grupo">
                                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeletarGrupo(grupo); }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>

                            {/* Tarefas agrupadas por módulo */}
                            <Collapse in={!!expandedGrupos[grupo.id_kanban_grupo]}>
                                <Box sx={{ p: 1.5 }}>
                                    {modulosTarefas.map(([moduloId, tarefas], mIdx) => (
                                        <Box key={moduloId} sx={{ mb: mIdx < modulosTarefas.length - 1 ? 1.5 : 0 }}>
                                            <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                                                <ViewKanbanIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                    {getModuloNome(moduloId)}
                                                </Typography>
                                            </Stack>
                                            {tarefas.map((tarefa, idx) => (
                                                <Box key={tarefa.id_kanban_tarefa}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        spacing={1}
                                                        sx={{ py: 0.5, px: 1.5 }}
                                                    >
                                                        <AssignmentIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                        <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>
                                                            {tarefa.nome_tarefa}
                                                        </Typography>
                                                        {tarefa.tempo_execucao_horas > 0 && (
                                                            <Chip
                                                                size="small"
                                                                icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                                                                label={`${tarefa.tempo_execucao_horas}h`}
                                                                sx={{ fontSize: 11, height: 20, bgcolor: '#e3f2fd', color: '#1565c0' }}
                                                            />
                                                        )}
                                                    </Stack>
                                                    {idx < tarefas.length - 1 && <Divider sx={{ ml: 4 }} />}
                                                </Box>
                                            ))}
                                            {mIdx < modulosTarefas.length - 1 && <Divider sx={{ mt: 1 }} />}
                                        </Box>
                                    ))}
                                </Box>
                            </Collapse>
                        </Paper>
                    );
                })}
            </Stack>
        </>
    );
}
